// Which price tier a visitor sees, and what it is called.
//
// Single source of truth on purpose: the tiers were previously hardcoded in
// Payment.vue and the marketing copy on WishboardCreate.vue had its own numbers
// ("Up to 50 messages", "Unlimited messages"), which were both wrong and
// unrelated to what the backend enforces. Anything showing a price or a limit
// should use these.
//
// The amounts themselves are NOT decided here. The backend looks up the real
// figures in PRICE_TIERS from a price_key, so a buyer cannot choose what they are
// charged. This file only decides which key to send and what to display.

// Regions kept at the USD price. Everyone else is offered USD as well, because an
// English-locale visitor in Australia or Singapore has no reason to hold rupees.
export const WESTERN_REGIONS = [
  'US', 'CA',
  'GB', 'IE', 'FR', 'DE', 'IT', 'ES', 'PT', 'NL', 'BE', 'AT', 'CH',
  'DK', 'SE', 'NO', 'FI', 'IS', 'PL', 'CZ', 'SK', 'HU', 'RO', 'BG',
  'GR', 'HR', 'SI', 'EE', 'LV', 'LT', 'LU', 'MT', 'CY', 'RS', 'UA',
  // Anglosphere outside the euro band. Australia, New Zealand and Ireland's
  // kin paid in dollars before and should not suddenly be offered rupees:
  // en-AU and en-NZ were falling through to "USD or INR", which put a rupee
  // option in front of an Australian. Ireland was already here.
  'AU', 'NZ',
];

// Wish limits, kept in step with FREE_WISH_LIMIT / ABSOLUTE_MAX_WISHES in the
// backend's settings.py. These are display values only.
export const FREE_WISH_LIMIT = 10;
export const PREMIUM_WISH_LIMIT = 200;

// Fallback labels, used until /api/config/ responds. The real ones come from the
// backend so a price change does not need a frontend release.
const FALLBACK_LABELS = { western: '$2.99', intl: '\u20b999' };

export function detectRegion() {
  const tags = (navigator.languages && navigator.languages.length)
    ? navigator.languages
    : [navigator.language || ''];
  for (const tag of tags) {
    const region = (tag.split('-')[1] || '').toUpperCase();
    if (!region) continue;
    return {
      region: region,
      isWestern: WESTERN_REGIONS.indexOf(region) !== -1,
    };
  }
  // No region subtag: offer both tiers and default to dollars rather than
  // guessing, since guessing INR would undercharge a Western visitor.
  return { region: '', isWestern: false };
}

// priceKey          the key to send to /api/payment/
// showBothTiers      whether the INR option is offered at all
// priceLabel        what to display for the chosen key
// The backend publishes price_tiers as {key: {label, currency}}. Accept a bare
// string too, so a caller passing either shape gets the label rather than an
// object that renders as [object Object].
function labelOf(entry, fallback) {
  if (!entry) return fallback;
  if (typeof entry === 'string') return entry;
  if (typeof entry.label === 'string' && entry.label) return entry.label;
  return fallback;
}

export function pricingForBuyer(tierLabels) {
  const { region, isWestern } = detectRegion();
  const src = tierLabels || {};
  const labels = {
    western: labelOf(src.western, FALLBACK_LABELS.western),
    intl: labelOf(src.intl, FALLBACK_LABELS.intl),
  };
  return {
    region: region,
    isWestern: isWestern,
    priceKey: 'western',
    priceLabel: labels.western,
    showBothTiers: !isWestern,
    intlLabel: labels.intl,
  };
}

// The plan copy on the create page. "Unlimited" is deliberately not used: 200 is
// the real ceiling, set because SQLite allows a single writer and the database is
// one file. Advertising "unlimited" would promise something we cannot deliver.
export function planCopy() {
  // "Up to N messages" rather than a bare "N messages". A bare number reads as a
  // quota rather than a ceiling, and next to Free's 10 it looked like Premium
  // boards hold exactly 200. They accept any number up to 200.
  return {
    freeName: 'Free',
    freeDetail: 'Up to ' + FREE_WISH_LIMIT + ' messages',
    freeNote: 'Enough for a small celebration',
    premiumName: 'Premium',
    premiumDetail: 'Up to ' + PREMIUM_WISH_LIMIT + ' messages',
    premiumNote: 'For the whole crowd \u00b7 one payment, unlocks this board',
  };
}

// A board's recipient line is only worth showing when the title does not already
// name them. Titles like "Happy Birthday Keerthana Shri!" above "For Keerthana
// Shri" repeat the name for no gain -- 203 of the boards on record are in that
// state, and the create form's placeholders used to suggest exactly that shape.
//
// The title is never rewritten and nothing is stored differently: this is purely
// a rendering decision, so existing boards improve with no migration.
export function recipientAddsInfo(title, recipient) {
  const who = (recipient || '').trim();
  if (!who) return false;

  const what = (title || '').trim();
  if (!what) return true;

  // Plain substring match, case-insensitive.
  //
  // An earlier version used \b word boundaries, which was wrong twice over:
  // \b is defined in terms of \w, so it never matches around non-ASCII scripts
  // -- a Malayalam name never matched its own title -- and it does not exist at a
  // string edge, so a name ending in punctuation ("Ravi (Ravi)", "Ravi.")
  // never matched either. Both are common enough to matter: this is a display
  // decision on real user data, and the failure mode is showing a duplicate line.
  //
  // Substring matching is more permissive than a word-boundary test in one
  // direction: a recipient of "Ann" is suppressed by a title reading "Joanne",
  // because "ann" occurs inside "joanne". Rather than reintroduce \b -- which
  // fails on non-ASCII scripts and on names ending in punctuation -- the trailing
  // edge is checked directly. A match only counts when the character after it is
  // not a letter or digit.
  //
  // The leading edge is left alone: "Ann" in "Joanne" is genuinely ambiguous, and
  // suppressing a redundant line is the harmless direction. Getting it wrong this
  // way means a name is not repeated in the header; getting it wrong the other way
  // means it is printed twice.
  const at = what.toLowerCase().indexOf(who.toLowerCase());
  if (at === -1) return true;

  const after = what[at + who.length];
  if (after === undefined) return false;          // name runs to the end
  return /[\p{L}\p{N}]/u.test(after);
}
